import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { SHOWREEL } from "../../data/content";
import { RevealLines } from "./primitives";

export const Hero = ({ onScrollCue }) => {
  const { videoSources, poster, tagline } = SHOWREEL;

  return (
    <section id="showreel" className="relative h-[100svh] w-full overflow-hidden" data-testid="hero">
      {/* Background showreel */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={poster}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="hero-showreel-video"
        >
          {videoSources.map((s) => (
            <source key={s} src={s} type="video/mp4" />
          ))}
        </video>
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/30 to-background" />
      <div className="absolute inset-0 bg-background/40" />

      {/* Top meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute top-20 md:top-28 left-4 md:left-10 right-4 md:right-10 flex items-center justify-between z-10"
      >
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--accent))] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--accent))]"></span>
          </span>
          <span className="kicker text-[9px] md:text-xs">Based in India · Working Worldwide</span>
        </div>
        <span className="kicker hidden sm:inline">Now Playing — Showreel</span>
      </motion.div>

      {/* Headline */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-4 md:px-10">
        <h1 className="display-h1 font-bungee" style={{ fontFamily: "'Bungee', sans-serif", lineHeight: 1.02 }} data-testid="hero-title">
          <RevealLines lines={["CF Action", "Design"]} delay={0.3} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-4 md:mt-6 max-w-xl font-mono-c text-[11px] sm:text-xs md:text-sm tracking-[0.12em] md:tracking-[0.15em] uppercase text-muted-foreground leading-relaxed"
        >
          {tagline}
        </motion.p>
      </div>

      {/* Scroll cue */}
      <motion.button
        onClick={onScrollCue}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 md:bottom-8 right-4 md:right-10 z-10 flex items-center gap-2 md:gap-3 group active:scale-95 transition-transform"
        data-testid="hero-scroll-cue"
      >
        <span className="kicker text-[9px] md:text-xs group-hover:text-foreground transition-colors">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          className="flex h-8 w-8 md:h-9 md:w-9 items-center justify-center border border-border rounded-full group-hover:border-[hsl(var(--accent))] transition-colors bg-background/40 backdrop-blur-sm"
        >
          <ArrowDown size={14} className="md:w-4 md:h-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};
